import React from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "boneyard-js/react";
import { Seller } from "../types";
import { Utils } from "../utils";
import { USER_BADGE } from "../utils/enums";
import { usePersistStore } from "../store";
import Avatar from "./Avatar";
import FollowButton from "./FollowButton";

interface InputProps {
  seller: Seller;
  showFollowButton?: boolean;
}

const SellerCard: React.FC<InputProps> = ({ seller, showFollowButton }) => {
  const { user, token } = usePersistStore((s: any) => s);
  const isOwnCard =
    !!token && !!user?._id && !!seller?._id && String(user._id) === String(seller._id);
  const locationText = [seller?.city?.trim(), seller?.state?.trim()]
    .filter(Boolean)
    .join(", ");
  const distanceText = seller?.distance
    ? `${Utils.formatToNumber2(seller.distance.toString())} mi`
    : "";
  const locationAndDistance = [locationText, distanceText]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="custom-col sellerCardCol mb-sm-4">
      {/* Wrap so the boneyard CLI can capture `seller-card` bones from
          the public /sellers page. Loading state never reaches here —
          consumers render <Skeleton name="seller-card" loading={true}>
          placeholders during fetch.

          style={{ height: "100%" }} on the Skeleton wrapper is needed
          because boneyard injects a <div style="position:relative">
          between this column and the inner card. Without an explicit
          height, that wrapper sizes to its content instead of the
          column, which breaks `.sellerCardModern { height: 100% }` —
          cards with less content (e.g. the viewer's own card with
          no Follow button) end up shorter than siblings. */}
      <Skeleton
        name="seller-card"
        loading={false}
        className="sellerCardSkeletonWrap"
      >
      <Link to={seller?.profileUrl} className="sellerCardModern">
        <div className="sellerCardModern__avatarWrap">
          <Avatar
            src={seller?.imageUrl}
            name={seller?.name}
            alt={seller?.name}
            className="sellerCardModern__avatar"
          />
        </div>
        <h3 className="sellerCardModern__name" title={seller?.name}>
          <span className="sellerCardModern__nameText">{seller?.name}</span>
          {seller?.badges?.includes(USER_BADGE.VERIFIED) && (
            <i
              className="bi bi-patch-check-fill sellerCardModern__verified"
              aria-label="Verified seller"
              title="Verified"
            />
          )}
        </h3>
        <div className="sellerCardModern__pills">
          {typeof seller?.sold === "number" && seller.sold > 0 && (
            <span className="sellerCardModern__pill sellerCardModern__pill--sales">
              <i className="bi bi-trophy" aria-hidden="true" />
              {seller.sold} {seller.sold === 1 ? "sale" : "sales"}
            </span>
          )}
          {seller?.speciality && (
            <span
              className="sellerCardModern__pill"
              title={seller.speciality}
            >
              <i className="bi bi-palette" aria-hidden="true" />
              {seller.speciality}
            </span>
          )}
        </div>
        <div className="sellerCardModern__meta">
          {locationAndDistance && (
            <div
              className="sellerCardModern__metaRow"
              title={locationAndDistance}
            >
              <i className="bi bi-geo-alt" aria-hidden="true" />
              <span>{locationAndDistance}</span>
            </div>
          )}
        </div>
        {showFollowButton && seller?._id && (
          <div
            className="sellerCardModern__actions"
            onClick={(e) => {
              if (isOwnCard) return;
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {isOwnCard ? (
              <span className="sellerCardModern__youBadge">
                <i className="bi bi-person-check" aria-hidden="true" />
                This is you
              </span>
            ) : (
              <FollowButton sellerId={seller._id} />
            )}
          </div>
        )}
      </Link>
      </Skeleton>
    </div>
  );
};

export default SellerCard;
